import { createSlice, PayloadAction } from '@reduxjs/toolkit';

const defaultSelectedApiKey =  { 
  id: 0, 
  posTypeId: 0, 
  posVendorName: 'None', 
  apiKey: '', 
  locationId: undefined, 
  isActive: true, 
  callbackUrl: null,
  giftcardCallbackUrl: null,
  markLocationBookingsLocked: false,
  oAuthRefreshToken: '',
  companyId: '',
  isBookingsIntegrationEnabled: false,
  isGiftCardsIntegrationEnabled: false
} as IApiKey;

const initialState: ApiKeyState = {
  isModalOpen: false,
  apiKeys: [],
  selectedApiKey: defaultSelectedApiKey,
  partners: [],
  partnerSites: [] as IPartnerSite[],
  selectedPartnerSite: null as IPartnerSite,
};

export interface IPartner{
  id: number,
  name: string,
  markBookingsLocked: boolean,
  clientId: string,
  enablePartnerOAuthFlow: boolean
};

export interface IApiKey {
  id: number,
  posTypeId: number,
  posVendorName: string,
  apiKey: string,
  locationId: string,
  isActive: boolean,
  dateCreated: Date,
  dateUpdated: Date,
  callbackUrl?: string | null,
  giftcardCallbackUrl?: string | null,
  markLocationBookingsLocked: boolean,
  oAuthRefreshToken: string,
  giftcardCallbackUrlSecurityToken: string,
  companyId: string,
  isBookingsIntegrationEnabled: boolean | false,
  isGiftCardsIntegrationEnabled: boolean | false,
};

export interface IApiKeyRequest {
    id: number,
    posVendorName: string,
    locationId: string,
    markLocationBookingsLocked: boolean,
    callbackUrl?: string | null,
    giftcards?: IWebhookRequest | null,
    oAuthRefreshToken: string,
    companyId: string,
    isBookingsIntegrationEnabled: boolean | false,
    isGiftCardsIntegrationEnabled: boolean | false
};

export interface IWebhookRequest {

    webhookUrl: string,
    securityToken: string
};

export interface IOAuthFlowAccessTokenResult {
  access_token: string,
  refresh_token: string,
  expires_in: number,
  token_type: string,
}

export interface IPartnerSite {
  id: string;
  company_id: string;
  merchant_id: string
  name: string;
  code: string;
  businessNumber: string;
  email: string;
  mobile: string;
  phone: string;
  fax: string;
}

export interface ApiKeyState {
  isModalOpen: boolean,
  apiKeys: IApiKey[],
  selectedApiKey: IApiKey,
  partners: IPartner[],
  partnerSites: IPartnerSite[],
  selectedPartnerSite: IPartnerSite
}

export const apiKeySlice = createSlice({
  name: 'ApiKeys',
  initialState,
  reducers: {
    handleModalToggle: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isModalOpen: action.payload
      };
    },
    updateSelectedApiKey: (state, action: PayloadAction<IApiKey>) => {
      return {
        ...state,
        selectedApiKey: action.payload
      };
    },
    resetSelectedApiKey: (state, action: PayloadAction) => {
      return {
        ...state,
        selectedApiKey: defaultSelectedApiKey
      };
    },
    getAll: (state, action: PayloadAction<IApiKey[]>) => {
      return {
        ...state,
        apiKeys: action.payload,
        selectedApiKey: defaultSelectedApiKey
      };
    },
    add: (state, action: PayloadAction<IApiKey>) => {
      const newApiKey = {...action.payload, isActive: true} as IApiKey;

      return {
        ...state,
        apiKeys: [...state.apiKeys, newApiKey],
        selectedApiKey: defaultSelectedApiKey
      };
    },
    remove: (state, action: PayloadAction<IApiKey>) => {
      const itemIndex = state.apiKeys.findIndex(f => f.id == action.payload.id);
      return {
        ...state,
        apiKeys: [
          ...state.apiKeys.slice(0, itemIndex), 
          ...state.apiKeys.slice(itemIndex + 1)
        ]
      };
    },
    edit: (state, action: PayloadAction<IApiKey>) => {
      const itemIndex = state.apiKeys.findIndex(f => f.id == action.payload.id);
      return {
        ...state,
        apiKeys: [
          ...state.apiKeys.slice(0, itemIndex),
          action.payload,
          ...state.apiKeys.slice(itemIndex + 1)
        ]
      };
    },
    getPartners: (state, action: PayloadAction<IPartner[]>) => {
      return {
        ...state,
        partners: action.payload
      };
    },
    addPartner: (state, action: PayloadAction<IPartner>) => {
      return {
        ...state,
        partners: [
          ...state.partners,
          action.payload
        ]
      }
    },
    setPartnerSites: (state, action: PayloadAction<IPartnerSite[]>) => {
      return {
        ...state,
        partnerSites: action.payload
      };
    }
  }
});

export const { getAll, add, remove, edit, handleModalToggle, resetSelectedApiKey, updateSelectedApiKey, getPartners, addPartner, setPartnerSites } = apiKeySlice.actions;

export default apiKeySlice.reducer;